// Dettaglio cliente con siti, contratti, referenti e storico
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Trash2, Pencil } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { PageHeader } from '../../components/layout/PageHeader'
import { Tabs, TabsContent } from '../../components/ui/Tabs'
import { Alert } from '../../components/ui/Alert'
import { Skeleton } from '../../components/ui/Skeleton'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Input } from '../../components/ui/Input'
import { SitesList } from './components/SitesList'
import { ContractsList } from './components/ContractsList'
import { SubcontractorsList } from './components/SubcontractorsList'
import api from '../../lib/api'

const TABS = [
  { value: 'info', label: 'Informazioni' },
  { value: 'sites', label: 'Sedi' },
  { value: 'contracts', label: 'Contratti' },
  { value: 'history', label: 'Storico' },
  { value: 'subcontracting', label: 'Subappalto' },
]

interface CompanyDetail {
  id: string
  name: string
  companyType?: string
  parentCompanyId?: string | null
  vatNumber?: string
  fiscalCode?: string
  sdiCode?: string
  pecEmail?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  postalCode?: string
  website?: string
  notes?: string
  isActive?: boolean
}


interface CompanyEditFormData {
  name: string
  vatNumber: string
  fiscalCode: string
  sdiCode: string
  email: string
  pecEmail: string
  phone: string
  address: string
  city: string
  postalCode: string
  website: string
  notes: string
}

function CompanyEditModal({ company, open, onClose }: { company: CompanyDetail; open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const { register, handleSubmit, formState: { errors } } = useForm<CompanyEditFormData>({
    defaultValues: {
      name: company.name ?? '',
      vatNumber: company.vatNumber ?? '',
      fiscalCode: company.fiscalCode ?? '',
      sdiCode: company.sdiCode ?? '',
      email: company.email ?? '',
      pecEmail: company.pecEmail ?? '',
      phone: company.phone ?? '',
      address: company.address ?? '',
      city: company.city ?? '',
      postalCode: company.postalCode ?? '',
      website: company.website ?? '',
      notes: company.notes ?? '',
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: CompanyEditFormData) => {
      await api.patch(`/companies/${company.id}`, data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', company.id] })
      void qc.invalidateQueries({ queryKey: ['companies'] })
      onClose()
    },
  })

  return (
    <Modal open={open} onClose={onClose} title="Modifica cliente" size="lg">
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Ragione sociale" required error={errors.name?.message} {...register('name', { required: 'Obbligatorio' })} />
          <Input label="P.IVA" {...register('vatNumber')} />
          <Input label="Codice Fiscale" {...register('fiscalCode')} />
          <Input label="Codice SDI" {...register('sdiCode')} />
          <Input label="Email" type="email" {...register('email')} />
          <Input label="PEC" type="email" {...register('pecEmail')} />
          <Input label="Telefono" {...register('phone')} />
          <Input label="Sito web" {...register('website')} />
          <Input label="Indirizzo" {...register('address')} />
          <Input label="Città" {...register('city')} />
          <Input label="CAP" {...register('postalCode')} />
          <Input label="Note" {...register('notes')} />
        </div>
        {mutation.isError && <Alert variant="error">Errore durante il salvataggio.</Alert>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Annulla</Button>
          <Button type="submit" isLoading={mutation.isPending}>Salva</Button>
        </div>
      </form>
    </Modal>
  )
}

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)

  const { data: company, isLoading, error } = useQuery({
    queryKey: ['company', id],
    queryFn: async () => {
      const res = await api.get<CompanyDetail>(`/companies/${id}`)
      return res.data
    },
    enabled: Boolean(id),
  })

  const isSubcontractor = company?.companyType === 'subcontractor'

  const [tab, setTab] = useState<string | null>(null)
  const activeTab = tab ?? 'info'

  // Fetch parent company name for breadcrumb (only for subcontractors)
  const { data: parentCompany } = useQuery({
    queryKey: ['company', company?.parentCompanyId],
    queryFn: async () => {
      const res = await api.get<CompanyDetail>(`/companies/${company!.parentCompanyId}`)
      return res.data
    },
    enabled: isSubcontractor && Boolean(company?.parentCompanyId),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/companies/${id}`)
    },
    onSuccess: () => {
      if (isSubcontractor && company?.parentCompanyId) {
        navigate(`/companies/${company.parentCompanyId}`)
      } else {
        navigate('/companies')
      }
    },
  })

  if (error) return <Alert variant="error">Impossibile caricare il cliente.</Alert>
  if (isLoading) return <Skeleton height={300} className="rounded-lg" />
  if (!company) return <Alert variant="error">Cliente non trovato.</Alert>

  const infoFields = [
    { label: 'Ragione sociale', value: company.name },
    { label: 'P.IVA', value: company.vatNumber },
    { label: 'Codice Fiscale', value: company.fiscalCode },
    { label: 'Codice SDI', value: company.sdiCode },
    { label: 'Email', value: company.email },
    { label: 'PEC', value: company.pecEmail },
    { label: 'Telefono', value: company.phone },
    { label: 'Indirizzo', value: company.address },
    { label: 'Città', value: company.city },
    { label: 'CAP', value: company.postalCode },
    { label: 'Sito web', value: company.website },
    { label: 'Note', value: company.notes },
  ].filter(({ value }) => value)

  const actions = (
    <div className="flex gap-2">
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Pencil className="h-4 w-4" />}
        onClick={() => setEditOpen(true)}
      >
        Modifica
      </Button>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Trash2 className="h-4 w-4 text-[var(--fc-danger)]" />}
        onClick={() => {
          const msg = isSubcontractor
            ? `Eliminare il subappaltato "${company.name}"?`
            : `Eliminare il cliente "${company.name}" e tutti i suoi subappalti?`
          if (window.confirm(msg)) deleteMutation.mutate()
        }}
        disabled={deleteMutation.isPending}
      >
        {isSubcontractor ? 'Elimina subappaltato' : 'Elimina cliente'}
      </Button>
    </div>
  )

  // Subappaltato: pagina semplice — solo sedi, nessun tab
  if (isSubcontractor) {
    const breadcrumb = [
      { label: 'Clienti', href: '/companies' },
      ...(parentCompany ? [{ label: parentCompany.name, href: `/companies/${company.parentCompanyId}` }] : []),
      { label: company.name },
    ]
    return (
      <div className="space-y-4">
        <PageHeader title={company.name} breadcrumb={breadcrumb} actions={actions} />
        <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4">
          {id && <SitesList companyId={id} />}
        </div>
        <CompanyEditModal company={company} open={editOpen} onClose={() => setEditOpen(false)} />
      </div>
    )
  }

  // Cliente master: pagina completa con tutti i tab
  const breadcrumb = [{ label: 'Clienti', href: '/companies' }, { label: company.name }]
  return (
    <div className="space-y-4">
      <PageHeader title={company.name} breadcrumb={breadcrumb} actions={actions} />

      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden">
        <Tabs tabs={TABS} value={activeTab} onValueChange={setTab}>

          <TabsContent value="info" className="p-4">
            <div className="space-y-2 text-sm">
              {infoFields.map(({ label, value }) => (
                <div key={label} className="flex gap-4">
                  <span className="w-36 shrink-0 text-[var(--fc-foreground-muted)]">{label}</span>
                  <span className="text-[var(--fc-foreground)] break-all">{value}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="sites" className="p-4">
            {id && <SitesList companyId={id} />}
          </TabsContent>

          <TabsContent value="contracts" className="p-4">
            {id && <ContractsList companyId={id} />}
          </TabsContent>

          <TabsContent value="history" className="p-4">
            <p className="text-sm text-[var(--fc-foreground-muted)]">
              Storico interventi — funzionalità in sviluppo
            </p>
          </TabsContent>

          <TabsContent value="subcontracting" className="p-4">
            {id && company && (
              <SubcontractorsList parentCompanyId={id} parentCompanyName={company.name} />
            )}
          </TabsContent>

        </Tabs>
      </div>
      <CompanyEditModal company={company} open={editOpen} onClose={() => setEditOpen(false)} />
    </div>
  )
}
